/**
 * LocalStorage Utilities
 * Type-safe localStorage with JSON serialization
 */

export class Storage {
  private static prefix = 'shopify_toolkit_';

  /**
   * Get item from localStorage with type safety
   */
  static get<T>(key: string): T | null {
    try {
      const item = localStorage.getItem(this.prefix + key);
      if (!item) return null;
      return JSON.parse(item) as T;
    } catch (error) {
      console.error(`Failed to get item from storage: ${key}`, error);
      return null;
    }
  }

  /**
   * Set item in localStorage
   */
  static set<T>(key: string, value: T): void {
    try {
      localStorage.setItem(this.prefix + key, JSON.stringify(value));
    } catch (error) {
      console.error(`Failed to set item in storage: ${key}`, error);
    }
  }

  /**
   * Remove item from localStorage
   */
  static remove(key: string): void {
    try {
      localStorage.removeItem(this.prefix + key);
    } catch (error) {
      console.error(`Failed to remove item from storage: ${key}`, error);
    }
  }

  /**
   * Clear all toolkit items from localStorage
   */
  static clear(): void {
    try {
      const keys = Object.keys(localStorage);
      keys.forEach((key) => {
        if (key.startsWith(this.prefix)) {
          localStorage.removeItem(key);
        }
      });
    } catch (error) {
      console.error('Failed to clear storage', error);
    }
  }

  /**
   * Check if key exists
   */
  static has(key: string): boolean {
    return localStorage.getItem(this.prefix + key) !== null;
  }
}

/**
 * Recently Viewed Products Manager
 */
export class RecentlyViewed {
  private static key = 'recently_viewed';
  private static maxItems = 10;

  static add(productId: number): void {
    const items = this.get();
    const filtered = items.filter((id) => id !== productId);
    filtered.unshift(productId);

    if (filtered.length > this.maxItems) {
      filtered.pop();
    }

    Storage.set(this.key, filtered);
  }

  static get(): number[] {
    return Storage.get<number[]>(this.key) || [];
  }

  static clear(): void {
    Storage.remove(this.key);
  }
}

/**
 * Notify Me Submissions Manager
 */
export interface NotifySubmission {
  email: string;
  variantId: number;
  productTitle: string;
  variantTitle: string;
  timestamp: string;
}

export class NotifyMeStorage {
  private static key = 'notify_submissions';

  static add(submission: NotifySubmission): void {
    const items = this.getAll();
    items.push(submission);
    Storage.set(this.key, items);
  }

  static getAll(): NotifySubmission[] {
    return Storage.get<NotifySubmission[]>(this.key) || [];
  }

  static hasPendingForVariant(variantId: number): boolean {
    const items = this.getAll();
    return items.some((item) => item.variantId === variantId);
  }

  static clear(): void {
    Storage.remove(this.key);
  }
}
